import { BrowserProvider, Contract, parseUnits } from 'ethers';
import { EthereumProvider } from '@walletconnect/ethereum-provider';
import { TOKEN_LABEL } from './format';

/**
 * MetaMask / injected-provider (EIP-1193) wallet connect helper for the
 * 2026-07-30 DApp registration/login flow, PLUS (2026-07-31) a WalletConnect
 * v2 path for wallets with no browser extension and no app-specific deep
 * link. This is the ONLY place in the user panel that talks to
 * window.ethereum / WalletConnect — Login.tsx and Register.tsx both go
 * through here so the connect/sign/network logic is not duplicated (and not
 * re-implemented slightly differently) between the two pages.
 *
 * Nothing here ever sees a private key — `signMessage` delegates to the
 * wallet extension's own signing UI, exactly like every other DApp.
 */

const BSC_MAINNET_CHAIN_ID_HEX = '0x38'; // 56 decimal

export class WalletConnectError extends Error {}

const WALLETCONNECT_PROJECT_ID = 'd9f05d3ab904cb58dcd25ecd4fdb72b1';

/**
 * The raw EIP-1193 provider currently "active" — set by whichever connect
 * path last succeeded. `connectWallet()` sets this to `window.ethereum`;
 * `connectWalletConnect()` sets it to the WalletConnect provider instance.
 * Both satisfy the same `request()`-based EIP-1193 shape, so
 * `signMessage()`/`sendLdsPayment()` can wrap whichever one is active in a
 * `BrowserProvider` identically, without needing to know which method the
 * user actually used to connect.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let activeRawProvider: any = null;

let wcProviderInstance: Awaited<ReturnType<typeof EthereumProvider.init>> | null = null;

async function getWalletConnectProvider() {
  if (wcProviderInstance) return wcProviderInstance;

  wcProviderInstance = await EthereumProvider.init({
    projectId: WALLETCONNECT_PROJECT_ID,
    chains: [56], // BSC mainnet — LDS is BEP-20
    showQrModal: true,
    metadata: {
      name: 'Alpha One',
      description: 'Alpha One wallet registration & login',
      url: 'https://app.alphaone.group',
      // Best-effort — apps/user has no public/ dir or bundled logo asset at
      // the time of writing; this matches the (also currently unresolved)
      // /favicon.ico referenced in index.html rather than inventing a new path.
      icons: ['https://app.alphaone.group/favicon.ico'],
    },
  });

  return wcProviderInstance;
}

/**
 * Connects via WalletConnect v2 (QR code / mobile wallet deep link chosen by
 * the user in the modal) — works for ANY compatible wallet, unlike the
 * MetaMask/Trust-Wallet-specific deep links, and needs no browser extension.
 * Returns the connected address, lowercased.
 */
export async function connectWalletConnect(): Promise<string> {
  const provider = await getWalletConnectProvider();

  try {
    await provider.connect();
  } catch (err) {
    if (err instanceof WalletConnectError) throw err;
    throw new WalletConnectError('Could not connect via WalletConnect. Please try again.');
  }

  const accounts = provider.accounts;
  if (!accounts?.[0]) {
    throw new WalletConnectError('No account was returned by WalletConnect.');
  }

  activeRawProvider = provider;
  return accounts[0].toLowerCase();
}

/**
 * Ends the current WalletConnect session (if any) and releases the cached
 * provider instance. Safe to call even when no WalletConnect session was ever
 * started. Intended for a page-unmount cleanup effect — leaving a WC session
 * open past navigation is not dangerous (no key material involved), but
 * disposing it is cheap and avoids a lingering socket connection.
 */
export async function disconnectWalletConnect(): Promise<void> {
  if (!wcProviderInstance) return;

  const instance = wcProviderInstance;
  wcProviderInstance = null;
  if (activeRawProvider === instance) {
    activeRawProvider = null;
  }

  try {
    await instance.disconnect();
  } catch {
    // Best-effort — the session may already be gone (e.g. the wallet app
    // ended it on its side), nothing to surface to the user here.
  }
}

/**
 * True when an EIP-1193 provider has been injected into `window.ethereum` —
 * true on desktop with the MetaMask (or similar) extension installed, and
 * true inside a wallet app's own in-app browser (Trust Wallet, MetaMask
 * mobile, etc.). False in a plain mobile Chrome/Safari tab, which has no
 * extension mechanism at all — that's the case the deep-link helpers below
 * exist for.
 */
export function hasInjectedProvider(): boolean {
  return typeof (window as unknown as { ethereum?: unknown }).ethereum !== 'undefined';
}

/** True on Android/iOS user agents — used to decide whether to surface the "open in wallet app" deep-link fallback UI. */
export function isMobileUserAgent(): boolean {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

/**
 * MetaMask's universal deep link: opens `url` inside the MetaMask mobile
 * app's own in-app browser, which injects `window.ethereum` the same way the
 * desktop extension does. See https://docs.metamask.io/wallet/how-to/link-to-metamask/
 */
export function metaMaskDeepLink(url: string): string {
  const stripped = url.replace(/^https?:\/\//, '');
  return `https://metamask.app.link/dapp/${stripped}`;
}

/**
 * Trust Wallet's universal deep link: opens `url` inside the Trust Wallet
 * app's own in-app browser. `coin_id=20000714` is Trust Wallet's registered
 * SLIP-44-style coin id for BNB Smart Chain (BSC is 20000714, distinct from
 * mainnet BNB Beacon Chain's 714) — matches the BSC deposit flow this app
 * already targets (BSC_MAINNET_CHAIN_ID_HEX above).
 * See https://developer.trustwallet.com/developer/develop-for-trust/deeplinking
 */
export function trustWalletDeepLink(url: string): string {
  return `https://link.trustwallet.com/open_url?coin_id=20000714&url=${encodeURIComponent(url)}`;
}

function getInjectedProvider() {
  const eth = (window as unknown as { ethereum?: unknown }).ethereum;

  if (!eth) {
    throw new WalletConnectError(
      'No wallet app was detected in this browser. Use the "Open in MetaMask" or "Open in Trust Wallet" ' +
        'buttons above to continue in a wallet app\'s browser, or install the MetaMask extension if you are on desktop.',
    );
  }

  activeRawProvider = eth;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return new BrowserProvider(eth as any);
}

/**
 * A `BrowserProvider` wrapping whichever raw EIP-1193 provider is currently
 * active (injected OR WalletConnect) — falls back to attempting the injected
 * provider if nothing has connected yet, matching the pre-existing behavior
 * of `signMessage()`/`sendLdsPayment()` for callers that somehow reach them
 * without having gone through `connectWallet()`/`connectWalletConnect()` first.
 */
function getActiveProvider() {
  if (!activeRawProvider) {
    return getInjectedProvider();
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return new BrowserProvider(activeRawProvider as any);
}

/** Requests account access and returns the connected address, lowercased. */
export async function connectWallet(): Promise<string> {
  const provider = getInjectedProvider();

  try {
    const accounts = await provider.send('eth_requestAccounts', []);
    if (!accounts?.[0]) {
      throw new WalletConnectError('No account was returned by the wallet.');
    }
    return String(accounts[0]).toLowerCase();
  } catch (err) {
    if (err instanceof WalletConnectError) throw err;
    const code = (err as { code?: number })?.code;
    if (code === 4001) {
      throw new WalletConnectError('Wallet connection was rejected.');
    }
    throw new WalletConnectError('Could not connect to the wallet. Please try again.');
  }
}

/** Signs `message` with `address` via the wallet's personal_sign — the exact scheme the backend verifies (EthSignatureVerifier). */
export async function signMessage(address: string, message: string): Promise<string> {
  const provider = getActiveProvider();

  try {
    const signer = await provider.getSigner(address);
    return await signer.signMessage(message);
  } catch (err) {
    const code = (err as { code?: number })?.code;
    if (code === 4001) {
      throw new WalletConnectError('Signature request was rejected. You must sign to continue.');
    }
    throw new WalletConnectError('Could not get a signature from the wallet. Please try again.');
  }
}

/** Best-effort prompt to switch to BSC mainnet (chain 56) — LDS is a BEP20 token. Failure is non-fatal; the backend re-validates the chain on submit. */
export async function ensureBscNetwork(): Promise<void> {
  const eth = (window as unknown as { ethereum?: { request: (args: unknown) => Promise<unknown> } }).ethereum;
  if (!eth) return;

  try {
    await eth.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: BSC_MAINNET_CHAIN_ID_HEX }] });
  } catch (err) {
    // 4902 = chain not added to the wallet yet — offer to add it.
    if ((err as { code?: number })?.code === 4902) {
      try {
        await eth.request({
          method: 'wallet_addEthereumChain',
          params: [
            {
              chainId: BSC_MAINNET_CHAIN_ID_HEX,
              chainName: 'BNB Smart Chain',
              nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
              rpcUrls: ['https://bsc-dataseed.binance.org/'],
              blockExplorerUrls: ['https://bscscan.com'],
            },
          ],
        });
      } catch {
        // Non-fatal — the member can switch manually; the backend still
        // verifies the actual on-chain transfer regardless of what network
        // the wallet UI is currently pointed at.
      }
    }
  }
}

const ERC20_TRANSFER_ABI = ['function transfer(address to, uint256 amount) returns (bool)'];
const LDS_DECIMALS = 18;

/**
 * Triggers an ERC20 `transfer(to, amount)` call on the LDS token contract via
 * the connected wallet's own signer — the wallet shows its native "confirm
 * this transaction" prompt, no manual copy-paste of an address required.
 * Per the client's explicit instruction ("AS SOON AS THE DAPP IS CONNECTED
 * THE RECEIVER ADDRESS IS AUTOMATICALLY FED FROM THE SYSTEM... CHAIN ID IS 56
 * AND THE ACCEPTABLE TOKEN IS LDS BEP20 TOKEN") — Register.tsx feeds
 * `tokenContractAddress`/`depositAddress`/`amountLds` straight from the
 * backend's registrationInfo() response, the user never sees or types an
 * address.
 *
 * Returns the transaction hash immediately after the wallet broadcasts it
 * (does NOT wait for on-chain confirmation — the backend's own
 * findRecentTransfer()/verify() already handles confirmation polling, and
 * blocking the UI here on a full confirmation would just be a slow, redundant
 * duplicate of what the backend already does after registration submits).
 */
export async function sendLdsPayment(
  address: string,
  tokenContractAddress: string,
  depositAddress: string,
  amountLds: string,
): Promise<string> {
  const provider = getActiveProvider();
  const signer = await provider.getSigner(address);
  const contract = new Contract(tokenContractAddress, ERC20_TRANSFER_ABI, signer);

  try {
    const tx = await contract.transfer(depositAddress, parseUnits(amountLds, LDS_DECIMALS));
    return tx.hash as string;
  } catch (err) {
    const code = (err as { code?: number | string })?.code;
    if (code === 4001 || code === 'ACTION_REJECTED') {
      throw new WalletConnectError('You rejected the payment transaction. Registration needs this transfer to proceed.');
    }
    throw new WalletConnectError(
      `Could not send the ${TOKEN_LABEL} payment. Check your wallet has enough ${TOKEN_LABEL} and BNB for gas, then try again.`,
    );
  }
}

export function shortAddress(address: string): string {
  return address.length > 12 ? `${address.slice(0, 6)}…${address.slice(-4)}` : address;
}

export function isValidAddress(address: string): boolean {
  return /^0x[a-fA-F0-9]{40}$/.test(address);
}

export function isValidTxHash(hash: string): boolean {
  return /^0x[a-fA-F0-9]{64}$/.test(hash);
}
